import { useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '../../utils/logger';
import { ALL_PROGRAMS_OPTION } from './constants';

export const useSnapshotModel = ({
  searchType,
  searchValue,
  fetchAllSnapshot,
  fetchDealerSnapshot,
  fetchProgramSnapshot,
  setPaginationModel,
  setDisplayedData,
  runLiveSearch,
  setTableSnackbar,
}) => {
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [snapshotDate, setSnapshotDateState] = useState('');
  const [snapshotContext, setSnapshotContext] = useState(null);
  const [snapshotErrors, setSnapshotErrors] = useState([]);
  const [snapshotFailures, setSnapshotFailures] = useState([]);
  const requestGeneration = useRef(0);
  const currentSearch = useRef({ searchType, searchValue });
  currentSearch.current = { searchType, searchValue };

  useEffect(() => {
    requestGeneration.current += 1;
    setIsSnapshotLoading(false);
    setSnapshotContext(null);
    setSnapshotErrors([]);
    setSnapshotFailures([]);
  }, [searchType, searchValue]);

  useEffect(() => () => {
    requestGeneration.current += 1;
  }, []);

  const setSnapshotDate = useCallback((value) => {
    requestGeneration.current += 1;
    setIsSnapshotLoading(false);
    setSnapshotErrors([]);
    setSnapshotFailures([]);
    setSnapshotDateState(value);
  }, []);

  const handleSnapshotLoad = useCallback(async () => {
    if (!snapshotDate) {
      setTableSnackbar({ open: true, severity: 'warning', message: 'Select a date before loading a snapshot.' });
      return;
    }

    setIsSnapshotLoading(true);
    setSnapshotErrors([]);
    setSnapshotFailures([]);
    const requestId = requestGeneration.current + 1;
    requestGeneration.current = requestId;
    const requestSearch = { searchType, searchValue };
    const isCurrentRequest = () => requestGeneration.current === requestId
      && currentSearch.current.searchType === requestSearch.searchType
      && currentSearch.current.searchValue === requestSearch.searchValue;

    try {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      if (searchType === 'program') {
        if (searchValue === ALL_PROGRAMS_OPTION) {
          const results = await fetchAllSnapshot(snapshotDate);
          if (!isCurrentRequest()) return;
          const combined = Array.isArray(results)
            ? results.flatMap((entry) => entry.data || [])
            : [];
          logger.info('Snapshot fetched for all programs', {
            snapshotDate,
            programCount: Array.isArray(results) ? results.length : 0,
            combinedRows: combined.length,
          });
          setDisplayedData(combined);
          const captureMap = results.map((entry) => ({
            programName: entry.programName,
            capturedAt: entry.data?.meta?.snapshotCapturedAt || entry.data?.meta?.cachedAt || null,
          }));
          setSnapshotContext({
            scope: 'all-programs',
            requested: snapshotDate,
            captures: captureMap,
          });
          setTableSnackbar({
            open: true,
            severity: 'success',
            message: 'Historical snapshot loaded for all programs.',
          });
        } else {
          const rows = await fetchProgramSnapshot(searchValue, snapshotDate);
          if (!isCurrentRequest()) return;
          logger.info('Snapshot fetched for program', {
            program: searchValue,
            snapshotDate,
            rows: Array.isArray(rows) ? rows.length : 0,
          });
          setDisplayedData(rows || []);
          setSnapshotContext({
            scope: 'program',
            requested: snapshotDate,
            captures: [{
              programName: searchValue,
              capturedAt: rows?.meta?.snapshotCapturedAt || rows?.meta?.cachedAt || null,
            }],
          });
          setTableSnackbar({
            open: true,
            severity: 'success',
            message: `Historical snapshot loaded for ${searchValue}.`,
          });
        }
      } else if (searchType === 'bac') {
        const results = await fetchDealerSnapshot(searchValue, snapshotDate);
        if (!isCurrentRequest()) return;
        const combined = Array.isArray(results)
          ? results.flatMap((entry) => entry.data || [])
          : [];
        // The bounded dealer endpoint already resolves and validates the normalized six-digit BAC.
        setDisplayedData(combined);
        const captureMap = results.map((entry) => ({
          programName: entry.programName,
          capturedAt: entry.data?.meta?.snapshotCapturedAt || entry.data?.meta?.cachedAt || null,
        }));
        setSnapshotContext({
          scope: 'bac',
          requested: snapshotDate,
          captures: captureMap,
        });
        setTableSnackbar({
          open: true,
          severity: 'success',
          message: `Historical snapshot loaded for BAC ${searchValue}.`,
        });
      }
    } catch (error) {
      if (!isCurrentRequest()) return;
      logger.error('Snapshot load failed', {
        message: error?.message,
        stack: error?.stack,
        code: error?.code,
      });
      const partial = error?.partialResults || [];
      if (partial.length > 0) {
        const combined = partial.flatMap((entry) => entry.data || []);
        setDisplayedData(combined);
        logger.warn('Snapshot partially loaded', {
          programsReturned: partial.length,
          combinedRows: combined.length,
        });
        const captureMap = partial.map((entry) => ({
          programName: entry.programName,
          capturedAt: entry.data?.meta?.snapshotCapturedAt || entry.data?.meta?.cachedAt || null,
        }));
        setSnapshotContext({
          scope: searchType === 'program' ? 'all-programs' : 'bac',
          requested: snapshotDate,
          captures: captureMap,
        });
      } else {
        setSnapshotContext(null);
        setDisplayedData([]);
        logger.warn('Snapshot load yielded no data', { snapshotDate, searchType, searchValue });
      }
      const programErrors = error?.programErrors || [];
      const programsMissing = programErrors
        .filter((item) => item.code === 'SNAPSHOT_NOT_FOUND')
        .map((item) => item.programName);
      const operationalFailures = programErrors.filter((item) => item.code !== 'SNAPSHOT_NOT_FOUND');
      setSnapshotErrors(programsMissing);
      setSnapshotFailures(operationalFailures);
      const failurePrograms = operationalFailures.map((item) => item.programName);
      const message = failurePrograms.length > 0
        ? `Historical snapshot failed for ${failurePrograms.join(', ')}. ${operationalFailures[0].message}`
        : programsMissing.length > 0
          ? `No snapshot available for ${programsMissing.join(', ')} on ${snapshotDate}.`
          : error?.message || 'Failed to load historical snapshot.';
      setTableSnackbar({ open: true, severity: 'error', message });
    } finally {
      if (isCurrentRequest()) setIsSnapshotLoading(false);
    }
  }, [
    fetchAllSnapshot,
    fetchDealerSnapshot,
    fetchProgramSnapshot,
    searchType,
    searchValue,
    setDisplayedData,
    setPaginationModel,
    setTableSnackbar,
    snapshotDate,
  ]);

  const handleSnapshotClear = useCallback(() => {
    setSnapshotDate('');
    setSnapshotContext(null);
    setSnapshotErrors([]);
    setSnapshotFailures([]);
    setIsSnapshotLoading(false);
    runLiveSearch();
    setTableSnackbar({ open: true, severity: 'info', message: 'Returned to live enrollment data.' });
  }, [runLiveSearch, setSnapshotDate, setTableSnackbar]);

  return {
    isSnapshotLoading,
    snapshotDate,
    setSnapshotDate,
    snapshotContext,
    setSnapshotContext,
    snapshotErrors,
    snapshotFailures,
    handleSnapshotLoad,
    handleSnapshotClear,
  };
};
